import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users } from "lucide-react";

interface Group {
  id: string;
  name: string;
  currency: string;
}

interface GroupContextType {
  groups: Group[];
  activeGroupId: string | null;
  activeGroup: Group | null;
  setActiveGroupId: (id: string) => void;
  loading: boolean;
}

const GroupContext = createContext<GroupContextType>({
  groups: [],
  activeGroupId: null,
  activeGroup: null,
  setActiveGroupId: () => {},
  loading: true,
});

export function GroupProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [groups, setGroups] = useState<Group[]>([]);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      // Try memberships first
      const { data: memberships } = await supabase
        .from("memberships")
        .select("group_id")
        .eq("user_id", user.id)
        .eq("status", "active")
        .eq("is_deleted", false);

      let groupIds = memberships?.map((m) => m.group_id) ?? [];

      // Admin fallback: load all groups
      if (groupIds.length === 0) {
        const { data: allGroups } = await supabase
          .from("groups")
          .select("id, name, currency")
          .eq("is_deleted", false)
          .order("name");
        if (allGroups && allGroups.length > 0) {
          setGroups(allGroups);
          const saved = localStorage.getItem("my-ajo-active-group");
          setActiveGroupId(saved && allGroups.some((g) => g.id === saved) ? saved : allGroups[0].id);
        }
      } else {
        const { data: userGroups } = await supabase
          .from("groups")
          .select("id, name, currency")
          .in("id", groupIds)
          .eq("is_deleted", false)
          .order("name");
        if (userGroups && userGroups.length > 0) {
          setGroups(userGroups);
          const saved = localStorage.getItem("my-ajo-active-group");
          setActiveGroupId(saved && userGroups.some((g) => g.id === saved) ? saved : userGroups[0].id);
        }
      }
      setLoading(false);
    })();
  }, [user]);

  const handleSetGroup = (id: string) => {
    setActiveGroupId(id);
    localStorage.setItem("my-ajo-active-group", id);
  };

  const activeGroup = groups.find((g) => g.id === activeGroupId) ?? null;

  return (
    <GroupContext.Provider value={{ groups, activeGroupId, activeGroup, setActiveGroupId: handleSetGroup, loading }}>
      {children}
    </GroupContext.Provider>
  );
}

export function useGroup() {
  return useContext(GroupContext);
}

export default function GroupSwitcher() {
  const { groups, activeGroupId, setActiveGroupId } = useGroup();

  if (groups.length <= 1) return null;

  return (
    <Select value={activeGroupId ?? ""} onValueChange={setActiveGroupId}>
      <SelectTrigger className="w-[180px] h-8 text-xs">
        <Users className="h-3.5 w-3.5 mr-1.5 shrink-0 text-muted-foreground" />
        <SelectValue placeholder="Select group" />
      </SelectTrigger>
      <SelectContent>
        {groups.map((g) => (
          <SelectItem key={g.id} value={g.id} className="text-xs">
            {g.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
